import { useEffect, useState } from "react";
import styled from "styled-components";

import { AppHeader } from "../components/app_header";
import { Container, Section, Title } from "../components/styled";
import { Coach as CoachModel } from "../model/Coach";
import { League as LeagueModel } from "../model/League";
import { useAuth } from "../hooks/useAuth";
import { useResource } from "../hooks/useResource";
import { Link, useParams } from "react-router-dom";

const EditButton = styled.p`
    margin: auto;
    text-decoration: underline;
    cursor: pointer;
`;

const LeaguesList = styled.div`
    margin-top: 1em;
`;

const LeagueItem = styled.div`
    padding: 0.5em;
    margin: 0.25em 0;
    border: 1px solid #ccc;
    border-radius: 4px;
    background-color: #f9f9f9;
`;

export const Coach = () => {
    const { id } = useParams();

    const [editMode, setEditMode] = useState(false);
    const [coachName, setCoachName] = useState<string>('');
    const [updateError, setUpdateError] = useState<string | null>(null);

    const { user, loading: authLoading, error: authError } = useAuth();

    const coachId = id || user?.username;
    
    const {
        data: coachData,
        loading: coachLoading,
        error: coachError,
        update,
    } = useResource<CoachModel>({
        resourceName: 'coaches',
        id: coachId,
        skipFetch: !coachId,
    });

    // Since we're fetching by ID, we expect a single coach
    const coach = Array.isArray(coachData) ? coachData[0] : coachData;

    // Fetch coach's leagues using the nested resource
    const {
        data: coachLeagues,
        loading: leaguesLoading,
        error: leaguesError,
    } = useResource<LeagueModel>({
        resourceName: 'leagues',
        parentResource: 'coaches',
        parentId: coachId,
        skipFetch: !coachId,
    });

    // Set coach name when data is loaded
    useEffect(() => {
        if (coach && !editMode) {
            setCoachName(coach.coachName);
        }
    }, [coach, editMode]);

    async function onSave() {
        if (!coach) {
            return;
        }
        
        const updatedCoach = {
            ...coach,
            coachName: coachName,
        };

        try {
            await update(coach.coachId, updatedCoach);
            setUpdateError(null);
            setEditMode(false);
        } catch (err) {
            setUpdateError(err instanceof Error ? err.message : 'Failed to update coach');
        }
    }

    const onEdit = () => {
        setUpdateError(null);
        setEditMode(!editMode);
    };

    // Show loading state while auth or coach data is loading
    if (authLoading || coachLoading) {
        return (
            <Container>
                <AppHeader />
                <Section>
                    Loading...
                </Section>
            </Container>
        );
    }

    // Show error state if there are auth or coach errors
    if (authError || coachError) {
        return (
            <Container>
                <AppHeader />
                <Section>
                    <Title>Error</Title>
                    <p>{authError || coachError}</p>
                </Section>
            </Container>
        );
    }

    // Show message if no coach data is available
    if (!coach) {
        return (
            <Container>
                <AppHeader />
                <Section>
                    <Title>Coach Not Found</Title>
                    <p>No coach data available.</p>
                </Section>
            </Container>
        );
    }

    return (
        <Container>
            <AppHeader />
            <Section>
                {updateError && (
                    <div style={{ color: 'red', marginBottom: '1em' }}>
                        {updateError}
                    </div>
                )}
                {!editMode &&
                    <Title>{coachName}</Title>
                }
                {editMode &&
                    <>
                        <label>Name: </label>
                        <input
                            value={coachName}
                            onChange={e => setCoachName(e.target.value)}/>
                        <button
                            onClick={onSave}
                            data-testid="save-edit-btn"
                        >
                            Save
                        </button>
                        <button
                            type="button"
                            onClick={() => setEditMode(false)}
                            data-testid="cancel-edit-btn"
                        >
                            Cancel
                        </button>
                    </>
                }
            </Section>
            {!editMode &&
                <EditButton onClick={onEdit}>(edit)</EditButton>
            }
            <Section>
                Id: {coach.coachId}
            </Section>
            {/* Always render leagues section */}
            <Section>
                <Title data-testid="leagues-section-title">Leagues</Title>
                {leaguesLoading && <p data-testid="leagues-loading">Loading leagues...</p>}
                {leaguesError && <p data-testid="leagues-error">Error loading leagues: {leaguesError}</p>}
                {!leaguesLoading && !leaguesError && (
                    <LeaguesList data-testid="leagues-list">
                        {Array.isArray(coachLeagues) && coachLeagues.length > 0 ? (
                            coachLeagues.map((league: LeagueModel) => (
                                <LeagueItem key={league.leagueId}>
                                    <Link to={`/leagues/${league.leagueId}`}>
                                        {league.leagueName}
                                    </Link>
                                </LeagueItem>
                            ))
                        ) : (
                            <p data-testid="no-leagues-message">No leagues found for this coach.</p>
                        )}
                    </LeaguesList>
                )}
            </Section>
        </Container>
    );
};